<script lang="ts">
  /**
   * The notices column above the bottom dock: messages newest first (older ones fade beneath),
   * then any takes fading in, each with its progress.
   */
  import { flip } from 'svelte/animate';
  import { fade, fly } from 'svelte/transition';
  import type { AppController } from '../../app/controller.svelte';
  import Icon from './Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  const toasts = $derived([...ctl.toasts].reverse());
  const takes = $derived([...ctl.takes].reverse());
  const reduced = $derived(ctl.settings.reducedMotion);
</script>

<div class="notices" aria-live="polite" data-testid="notices">
  {#each toasts as t, i (t.id)}
    <div
      class="toast"
      class:error={t.kind === 'error'}
      role={t.kind === 'error' ? 'alert' : 'status'}
      data-testid="toast"
      style:opacity={Math.max(0.3, 1 - i * 0.22)}
      in:fly={{ y: reduced ? 0 : -10, duration: reduced ? 0 : 180 }}
      out:fade={{ duration: reduced ? 0 : 250 }}
      animate:flip={{ duration: reduced ? 0 : 200 }}
    >
      <span class="msg">{t.message}</span>
      <button aria-label="Dismiss" onclick={() => ctl.dismissToast(t.id)}
        ><Icon name="close" /></button
      >
    </div>
  {/each}
  {#each takes as take (take.id)}
    {@const pos = ctl.takePosition(take)}
    <div
      class="take"
      class:held={!take.auto}
      data-testid="take-item"
      out:fade={{ duration: reduced ? 0 : 300 }}
      animate:flip={{ duration: reduced ? 0 : 200 }}
    >
      <div class="take-head">
        <span class="kind">{take.auto ? 'Fading in' : 'On the T-bar'}</span>
        <span class="name">{take.name}</span>
        <span class="pct">{Math.round(pos * 100)}%</span>
      </div>
      <div class="bar"><i style:width={`${pos * 100}%`}></i></div>
    </div>
  {/each}
</div>

<style>
  .notices {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: min(440px, 100%);
    max-height: 50vh;
    overflow: hidden;
    justify-content: flex-end;
  }
  .toast {
    pointer-events: auto;
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px 8px 14px;
    border-radius: var(--radius);
    background: rgba(20, 18, 32, 0.94);
    border: 1px solid var(--border);
    font-size: 13px;
    transition: opacity 0.3s;
  }
  .toast.error {
    background: rgba(40, 10, 16, 0.94);
    border-color: rgba(255, 107, 107, 0.45);
  }
  .toast button {
    border: none;
    background: none;
    padding: 2px 6px;
  }
  .take {
    padding: 7px 12px 8px;
    border-radius: var(--radius-sm);
    background: rgba(10, 24, 18, 0.9);
    border: 1px solid rgba(75, 227, 160, 0.35);
    font-size: 12px;
  }
  .take.held {
    background: rgba(32, 10, 14, 0.9);
    border-color: rgba(255, 107, 107, 0.4);
  }
  .take-head {
    display: flex;
    gap: 8px;
    align-items: baseline;
    margin-bottom: 5px;
  }
  .kind {
    color: var(--muted);
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: 0.06em;
  }
  .name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pct {
    font-family: var(--mono);
    color: var(--muted);
  }
  .bar {
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
  }
  .bar i {
    display: block;
    height: 100%;
    background: var(--ok);
  }
  .take.held .bar i {
    background: var(--danger);
  }
</style>
